/**
 * The money contract.
 *
 * The root cause of seventeen live inconsistencies is that card models accept
 * pre-formatted money STRINGS: once the text exists, no component can enforce
 * a glyph, a position, a grouping locale, a period or a scope, because the
 * formatting decision was already made in a pillar file. So money is never a
 * string in a model here. A model carries `Money`, `MoneyText`, `MoneyRange`
 * or `MoneyBound`, and `CresultcardMoney` owns every glyph.
 *
 * Grouping and rounding are separate operations, and separating them dissolves
 * the one real conflict between two pillars. Grouping inserts separators and
 * never changes digits: a captured `4428.2998046875` renders as
 * `4,428.2998046875`, and `145000` renders as `145,000`. Rounding is only ever
 * an explicit, per-call decision, and it exists on exactly one prop in the kit
 * (`CresultcardDelta`'s `precision`).
 */
import {
  CresultcardMoneyBoundKind,
  CresultcardPriceBasis,
  CresultcardPricePeriod,
} from "./cresultcard.types";

/** Pinned once. A bare `toLocaleString()` renders `523,13` for USD in a German browser. */
export const MONEY_LOCALE = "en-US";

/** `Intl` refuses more than this, and a capture past it is not a real figure. */
const MAX_FRACTION_DIGITS = 20;
const DECIMAL_POINT = ".";
const EXPONENT_MARKER = "e";
/** Hair space: the only thing between an ISO code and its figure. */
const CODE_GAP = " ";
const RANGE_DASH = "–";
const AT_LEAST_SUFFIX = "+";
const AT_MOST_PREFIX = "up to ";

/** An amount we captured as a number, with the currency it was captured in. */
export interface Money {
  readonly amount: number;
  readonly currency: string;
}

/**
 * A vendor figure we must not re-parse — `297,100`, `Ask Agent`,
 * `720,000 - 740,000`, `UNDER CONTRACT`. It is typed differently from `Money`
 * precisely so a component cannot double-prefix it.
 */
export interface MoneyText {
  readonly text: string;
  /** A source may state an amount without naming its currency. */
  readonly currency?: string;
}

export interface MoneyRange {
  readonly from: number;
  readonly to: number;
  readonly currency: string;
}

export interface MoneyBound {
  readonly amount: number;
  readonly currency: string;
  readonly bound: CresultcardMoneyBoundKind;
}

export type CresultcardPrice = Money | MoneyText | MoneyRange | MoneyBound;

/** Symbols only where they are unambiguous. Everything else keeps its code. */
const CURRENCY_SYMBOL: Readonly<Record<string, string>> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
};

/**
 * Notation a reader cannot be expected to know, where not knowing it is a
 * hundred-fold error. These are correctness affordances about the NOTATION,
 * carried as a title and an accessible label — never as inline body copy.
 */
const CURRENCY_GLOSS: Readonly<Record<string, string>> = {
  GBX: "pence sterling — one hundredth of a pound",
  ILA: "Israeli pound, withdrawn",
  JPY: "Japanese yen — no minor unit",
};

export const PRICE_PERIOD_LABEL: Record<CresultcardPricePeriod, string> = {
  [CresultcardPricePeriod.Night]: "/ night",
  [CresultcardPricePeriod.Year]: "/ yr",
  [CresultcardPricePeriod.Month]: "/ mo",
  [CresultcardPricePeriod.Hour]: "/ hr",
  [CresultcardPricePeriod.Day]: "/ day",
  [CresultcardPricePeriod.Person]: "/ person",
};

/** Inclusive vs exclusive is data. A component must never hardcode the claim. */
export const PRICE_BASIS_LABEL: Record<CresultcardPriceBasis, string | null> = {
  [CresultcardPriceBasis.Inclusive]: "tax incl.",
  [CresultcardPriceBasis.Exclusive]: "+ tax",
  [CresultcardPriceBasis.Unknown]: null,
};

function normalizeCode(currency: unknown): string {
  if (typeof currency !== "string") return "";
  const code = currency.trim().toUpperCase();
  return /^[A-Z]{3}$/u.test(code) ? code : "";
}

/** The symbol where one is unambiguous; the wire code otherwise. */
export function currencyUnit(currency: unknown): string {
  const code = normalizeCode(currency);
  return CURRENCY_SYMBOL[code] ?? code;
}

/** The gloss for a notation a reader cannot check, or nothing. */
export function currencyGloss(currency: unknown): string | undefined {
  return CURRENCY_GLOSS[normalizeCode(currency)];
}

function unitPrefix(currency: string): string {
  const code = normalizeCode(currency);
  if (code.length === 0) return "";
  const symbol = CURRENCY_SYMBOL[code];
  return symbol ?? `${code}${CODE_GAP}`;
}

/**
 * How many decimals the capture itself carried. Grouping must not invent
 * precision and must not drop it, so the count comes from the value rather
 * than from a house rule.
 */
function capturedFractionDigits(amount: number): number {
  const text = String(amount);
  if (text.includes(EXPONENT_MARKER)) {
    return MAX_FRACTION_DIGITS;
  }
  const point = text.indexOf(DECIMAL_POINT);
  if (point < 0) {
    return 0;
  }
  return Math.min(text.length - point - 1, MAX_FRACTION_DIGITS);
}

interface FormatMoneyFigureInput {
  readonly amount: number;
}

/**
 * Grouping only — no glyph. For a secondary figure INSIDE a block that has
 * already stated its currency, and for every other formatter here.
 */
export function formatMoneyFigure({ amount }: FormatMoneyFigureInput): string {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "";
  const digits = capturedFractionDigits(amount);
  return new Intl.NumberFormat(MONEY_LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

interface FormatMoneyInput {
  readonly value: number;
  readonly currency: string;
}

/**
 * The one glyph rule: symbol prefix where the symbol is unambiguous, ISO code
 * prefix with a hair space otherwise. Never a suffix code.
 *
 * It takes a NUMBER. Its predecessor took `string | number`, which is exactly
 * what made `typical 295–$600` writable: a pre-joined range handed to a
 * single-figure formatter gets a unit glued to its front and keeps the one
 * already inside it.
 */
export function formatMoney({ value, currency }: FormatMoneyInput): string {
  const figure = formatMoneyFigure({ amount: value });
  return figure.length === 0 ? "" : `${unitPrefix(currency)}${figure}`;
}

interface FormatMoneyRangeInput {
  readonly from: number;
  readonly to: number;
  readonly currency: string;
}

/** One unit, on the low end. En dash, no spaces. */
export function formatMoneyRange({
  from,
  to,
  currency,
}: FormatMoneyRangeInput): string {
  const low = formatMoney({ value: from, currency });
  const high = formatMoneyFigure({ amount: to });
  if (low.length === 0 || high.length === 0 || from > to) return "";
  return `${low}${RANGE_DASH}${high}`;
}

interface FormatMoneyBoundInput {
  readonly amount: number;
  readonly currency: string;
  readonly bound: CresultcardMoneyBoundKind;
}

export function formatMoneyBound({
  amount,
  currency,
  bound,
}: FormatMoneyBoundInput): string {
  const figure = formatMoney({ value: amount, currency });
  if (
    figure.length === 0 ||
    !Object.values(CresultcardMoneyBoundKind).includes(bound)
  )
    return "";
  return bound === CresultcardMoneyBoundKind.AtLeast
    ? `${figure}${AT_LEAST_SUFFIX}`
    : `${AT_MOST_PREFIX}${figure}`;
}

/** The field that discriminates each shape from the other three. */
const MONEY_TEXT_KEY = "text";
const MONEY_RANGE_KEY = "from";
const MONEY_BOUND_KEY = "bound";

function isPriceRecord(price: unknown): price is Record<string, unknown> {
  return typeof price === "object" && price !== null && !Array.isArray(price);
}

export function isMoneyText(price: unknown): price is MoneyText {
  return isPriceRecord(price) && Object.hasOwn(price, MONEY_TEXT_KEY);
}

export function isMoneyRange(price: unknown): price is MoneyRange {
  return isPriceRecord(price) && Object.hasOwn(price, MONEY_RANGE_KEY);
}

export function isMoneyBound(price: unknown): price is MoneyBound {
  return isPriceRecord(price) && Object.hasOwn(price, MONEY_BOUND_KEY);
}

/**
 * One figure from any of the four shapes. A `MoneyText` passes through
 * verbatim and gains no second unit — the vendor already wrote one, or
 * deliberately did not.
 */
export function formatCresultcardPrice(price: unknown): string {
  if (!isPriceRecord(price)) return "";
  if (isMoneyText(price)) {
    return typeof price.text === "string" && price.text.trim().length > 0
      ? price.text
      : "";
  }
  if (isMoneyRange(price)) {
    return formatMoneyRange(price);
  }
  if (isMoneyBound(price)) {
    return formatMoneyBound(price);
  }
  if (typeof price.amount !== "number") return "";
  return formatMoney({
    value: price.amount,
    currency: normalizeCode(price.currency),
  });
}
